import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { ArtData, SITE_DATA_PARTITIONS } from "../../../../types";
import { Api } from "../../api";
import { ArtList } from "../../components/ArtList";
import { useCart } from "../../providers/cart";
import { isSameArt } from "../../utils/art";
import { BUTTON_COLORS, BUTTON_TYPE } from "../../components/Button";
import { usePermissions } from "../../providers/authProvider";
import { UNDERLINE_CLASS } from "../../utils";
import {
  ART_LIST_URL,
  ART_URL,
  NEW_SLUG,
  URLS,
  USER_AUTH_URL,
} from "../../config";

export default function Art({
  admin = false,
  type = "gallery",
}: {
  admin?: boolean;
  type?: "gallery" | "in-progress";
}) {
  const { cart } = useCart();
  const permissions = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // State for data fetching
  const [artData, setArtData] = useState<ArtData[]>([]);
  const [bgImagePath, setBgImagePath] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // // Update document title
  // useEffect(() => {
  //   document.title = "Art | Doridium.com";
  // }, []);

  // Handle authentication for admin view
  useEffect(() => {
    if (admin) {
      if (!permissions?.data?.loggedIn) {
        navigate(
          USER_AUTH_URL("login", {
            flow: "resume",
            to: location.pathname + location.search,
          })
        );
        return;
      }
      if (!permissions?.data?.user?.admin) {
        navigate("/");
        return;
      }
    }
  }, [permissions, admin, navigate, location.pathname, location.search]);

  // Fetch art data on mount and when filters change
  useEffect(() => {
    async function fetchArtData() {
      setLoading(true);
      setError(null);

      try {
        const response = await Api.getAll<ArtData>({
          type: SITE_DATA_PARTITIONS.ART,
        });

        if (!response.success || !response.body?.items) {
          console.log("res", response);
          throw new Error("Failed to fetch art data");
        }

        // Filter art based on view mode
        const filteredArt = response.body.items.filter((art) =>
          admin
            ? true
            : art.isPublic &&
              (type === "in-progress"
                ? art.isInProgress
                : !art.isInProgress && !art.orderSlug)
        );

        setArtData(filteredArt);

        // Select random background image
        if (filteredArt.length > 0) {
          const randomArt =
            filteredArt[Math.floor(Math.random() * filteredArt.length)];
          setBgImagePath(randomArt.images?.[0]?.path ?? null);
        }

        setLoading(false);
      } catch (err: any) {
        console.error("Error fetching art:", err);
        setError(err.message ?? "Failed to load art");
        setLoading(false);
      }
    }

    fetchArtData();
  }, [admin, type]);

  // Error state
  if (error) {
    return (
      <Layout loading={loading}>
        <div className="text-center p-10">
          <div className="text-red-500 text-2xl">Error: {error}</div>
          <button
            onClick={() => window.location.reload()}
            className={BUTTON_TYPE.small + BUTTON_COLORS.primary}
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }
  console.log(loading, artData?.length);
  const getArtConfig = admin
    ? (art: ArtData) => ({
        href: ART_URL(art.slug, admin),
        text: (
          <>
            <span>{art.title}</span>
            <div>
              {art.isInProgress ? (
                <span className="text-xs">
                  FYI - This is art is in progress
                </span>
              ) : (
                <span className="text-xs">  </span>
              )}
              {!art.isPublic ? (
                <span className="text-xs">FYI - This is art is hidden</span>
              ) : (
                <span className="text-xs">  </span>
              )}
              <br />
              {art.orderSlug ? (
                <span className="text-xs">Sold!</span>
              ) : (
                <span className="text-xs">  </span>
              )}
            </div>
          </>
        ),
        showPrice: "below" as const,
        height: 250,
        tileClassName: art.orderSlug
          ? " bg-main text-textMain rounded-2xl border-solid border-2 border-textPrimary shadow-md hover:shadow-lg"
          : !art.isPublic
          ? " bg-accent text-textAccent rounded-2xl border-solid border-2 border-textPrimary shadow-md hover:shadow-lg"
          : art.isInProgress
          ? " bg-accent text-textAccent rounded-2xl border-solid border-2 border-textPrimary shadow-md hover:shadow-lg"
          : undefined,
      })
    : (art: ArtData) =>
        art.orderSlug
          ? {
              text: (
                <>
                  {art.title}
                  <br />
                  <p className="text-xs">Sold</p>
                </>
              ),
              href: ART_URL(art.slug, admin),
              showPrice: admin ? undefined : ("below" as const),
              // onAction: () => removeItem(art),
              // actionIcon: <>❎</>,
              // actionTitle: "Remove from cart",
              // actionColor: "none",
              // showPrice: admin ? undefined : "below",
            }
          : art.isInProgress
          ? {
              text: (
                <>
                  {art.title}
                  <br />
                  <p className="text-xs">In progress</p>
                </>
              ),
              href: ART_URL(art.slug, admin),
              // showPrice: admin ? undefined : "below",
              // onAction: () => removeItem(art),
              // actionIcon: <>❎</>,
              // actionTitle: "Remove from cart",
              // actionColor: "none",
              // showPrice: admin ? undefined : "below",
            }
          : cart.items.find((item) => isSameArt(item, art))
          ? {
              // text: (
              //   <>
              //     {art.title}
              //     <br />
              //     <p className="text-xs">In Cart</p>
              //   </>
              // ),
              href: ART_URL(art.slug, admin),

              // actionCorner: {
              //   onAction: () => undefined,
              //   actionIcon: (
              //     <div className="text-4xl">
              //       <Link
              //         href={CART_URL(cart?.slug)}
              //         // className={
              //         // BUTTON_TYPE.small +
              //         // BUTTON_COLORS.success
              //         // }
              //       >
              //         ✅
              //       </Link>
              //     </div>
              //   ),
              //   actionTitle: "Checkout",
              //   actionSize: "4rem",
              //   actionColor: "accent",
              //   actionCorner: "bottomRight",
              // },
              // showPrice: admin ? undefined : "below",

              // actionBelow: {
              //   actionComponent: (
              //     <div className="-mx-1">
              //       <Link
              //         href={CART_URL(cart?.slug)}
              //         className={
              //           BUTTON_TYPE.small + BUTTON_COLORS.primary
              //         }
              //       >
              //         Checkout
              //       </Link>

              //       <Button
              //         btnType="xs"
              //         btnColor="secondary"
              //         onClick={() => removeItem(art)}
              //       >
              //         Remove from cart
              //       </Button>
              //     </div>
              //   ),
              // },
            }
          : {
              // text: (
              //   <>
              //     {art.title}
              //     <br />
              //     {/* <p className="text-xs">  </p> */}
              //   </>
              // ),
              href: ART_URL(art.slug, admin),
              // showPrice: admin ? undefined : "below",

              // actionCorner: {
              //   actionIcon: <div className="text-4xl">🛒</div>,
              //   actionTitle: "Add to cart",
              //   actionColor: "accent",
              //   actionSize: "4rem",
              //   actionCorner: "bottomRight",
              //   onAction: () => addItem(art),
              // },
              // actionBelow: {
              //   actionComponent: (
              //     <div className="-mx-1">
              //       <Button
              //         btnType="xs"
              //         btnColor="primary"
              //         onClick={() => addItem(art)}
              //       >
              //         Add to cart
              //       </Button>
              //     </div>
              //   ),
              // },

              // onMouseEnter: () =>
              //   setHovered((h) => new Set(h).add(art.slug)),
              // onMouseLeave: () => {
              //   setHovered((h) => {
              //     h.delete(art.slug);
              //     return new Set(h);
              //   });
              // },
            };

  return (
    <Layout
      bgImage={
        bgImagePath
          ? "url(" + URLS.image(bgImagePath, { useImageSize: "size-xl" }) + ")"
          : undefined
      }
      loading={loading}
    >
      <h2 className="text-textMain text-center p-[10px] mb-[10px] text-4xl">
        The {admin ? "admin" : ""} gallery{" "}
        {!admin ? (
          <sub className="text-sm">
            {type === "in-progress" ? (
              <div className="my-1">
                <Link to={ART_LIST_URL("basic")} className={UNDERLINE_CLASS}>
                  Check out art that&apos;s ready to go right now!
                </Link>
              </div>
            ) : (
              <>
                <div className="my-1">
                  <Link
                    to={ART_LIST_URL("in-progress")}
                    className={UNDERLINE_CLASS}
                  >
                    View in progress art
                  </Link>
                </div>
              </>
            )}
          </sub>
        ) : null}
      </h2>

      <div className="flex justify-between m-5 px-2">
        {admin ? (
          <a
            className={BUTTON_TYPE.small + BUTTON_COLORS.primary}
            href={ART_URL(NEW_SLUG, admin)}
          >
            Post New Art
          </a>
        ) : null}
      </div>
      {loading ? (
        <>This is taking longer than usual, but we're working on it.</>
      ) : (
        <ArtList items={artData} getArtConfig={getArtConfig} />
      )}
    </Layout>
  );
}
